import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, MessageCircle, LogIn } from 'lucide-react';
import { waLink } from '../lib/whatsapp';

const links = [
  { label: 'Home', href: '#home' },
  { label: 'Services', href: '#services' },
  { label: 'Calculator', href: '#calculator' },
  { label: 'About', href: '#about' },
  { label: 'Contact', href: '#contact' },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-ink-900/80 backdrop-blur-2xl border-b border-white/5 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
        <a href="#home" className="flex items-center gap-3" data-testid="nav-logo">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-gold-dark to-gold-light flex items-center justify-center text-ink-900 font-display font-bold text-lg shadow-[0_0_20px_rgba(217,119,6,0.35)]">
            SN
          </div>
          <div className="leading-none">
            <span className="block font-display text-xl text-white tracking-tight">SN Associates</span>
            <span className="block text-[10px] text-slate-400 tracking-[0.25em] uppercase mt-0.5">Tax · Accounting · Advisory</span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9 text-sm text-slate-300 font-medium">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="relative hover:text-white transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-gold-light after:transition-all hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => navigate('/portal')}
            data-testid="nav-portal-button"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-gold/40 text-gold-light hover:bg-gold/10 transition-colors text-sm font-medium"
          >
            <LogIn className="w-4 h-4" strokeWidth={1.5} /> Client Portal
          </button>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-button"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold text-sm transition-colors shadow-[0_0_18px_rgba(37,211,102,0.35)]"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Chat on WhatsApp
          </a>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden text-slate-200"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-ink-800 border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="block text-slate-300 hover:text-gold-light font-medium"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => { setOpen(false); navigate('/portal'); }}
                className="w-full py-3 rounded-full border border-gold/40 text-gold-light font-medium"
                data-testid="mobile-portal-button"
              >
                Client Portal
              </button>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="block text-center w-full py-3 rounded-full bg-[#25D366] text-white font-semibold"
                data-testid="mobile-whatsapp-button"
              >
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
